import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Upload, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

interface AvatarPickerProps {
  currentUrl?: string | null;
  onSelect: (url: string) => void;
  userName?: string;
}

const AVATAR_STYLES = {
  professional: 'lorelei',
  business: 'notionists',
  fun: 'thumbs',
  neutral: 'big-ears-neutral',
  creative: 'adventurer',
  retro: 'pixel-art',
  geometric: 'shapes',
  classic: 'avataaars'
};

const SEEDS = [
  'Felix', 'Aneka', 'Midnight', 'Lucky'
];

// Generate 8 styles x 4 seeds = 32 avatars
const PRESET_AVATARS = Object.values(AVATAR_STYLES)
  .flatMap(style => 
    SEEDS.map(seed => 
      `https://api.dicebear.com/9.x/${style}/svg?seed=${seed}`
    )
  );

export const AvatarPicker = ({ currentUrl, onSelect, userName }: AvatarPickerProps) => {
  const [open, setOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(currentUrl || null);
  const [uploading, setUploading] = useState(false);
  const [generatedStyle, setGeneratedStyle] = useState('lorelei');
  const { user } = useAuth();

  const handlePresetSelect = (url: string) => {
    setSelectedAvatar(url);
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('File must be an image');
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/avatar-${Date.now()}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(data.path);

      setSelectedAvatar(publicUrl);
      toast.success('Avatar uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    if (selectedAvatar) {
      onSelect(selectedAvatar);
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Change Avatar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Choose Your Avatar</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="preset" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="preset">Choose Avatar</TabsTrigger>
            <TabsTrigger value="generate">Generate</TabsTrigger>
            <TabsTrigger value="upload">Upload Custom</TabsTrigger>
          </TabsList>

          <TabsContent value="preset" className="space-y-4">
            <div className="space-y-6 max-h-[500px] overflow-y-auto p-1">
              {Object.entries(AVATAR_STYLES).map(([category, style]) => (
                <div key={category}>
                  <h4 className="text-sm font-medium mb-2 capitalize text-muted-foreground">{category}</h4>
                  <div className="grid grid-cols-4 gap-3">
                    {SEEDS.map((seed, index) => {
                      const url = `https://api.dicebear.com/9.x/${style}/svg?seed=${seed}`;
                      return (
                        <button
                          key={index}
                          onClick={() => handlePresetSelect(url)}
                          className={`relative rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${
                            selectedAvatar === url
                              ? 'border-primary ring-2 ring-primary/20'
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <Avatar className="h-20 w-20">
                            <AvatarImage src={url} alt={`${category} ${seed}`} />
                            <AvatarFallback>{seed[0]}</AvatarFallback>
                          </Avatar>
                          {selectedAvatar === url && (
                            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                              <Check className="h-6 w-6 text-primary" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="generate" className="space-y-4">
            <div className="space-y-4 p-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Avatar Style</label>
                <select 
                  value={generatedStyle} 
                  onChange={(e) => setGeneratedStyle(e.target.value)}
                  className="w-full p-2 border rounded-md bg-background"
                >
                  {Object.entries(AVATAR_STYLES).map(([label, style]) => (
                    <option key={style} value={style}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center justify-center py-6">
                <Avatar className="h-32 w-32 border-4 border-primary/20">
                  <AvatarImage 
                    src={`https://api.dicebear.com/9.x/${generatedStyle}/svg?seed=${userName || 'User'}`}
                  />
                  <AvatarFallback className="text-2xl">
                    {userName?.[0] || 'U'}
                  </AvatarFallback>
                </Avatar>
              </div>
              
              <Button 
                onClick={() => {
                  const url = `https://api.dicebear.com/9.x/${generatedStyle}/svg?seed=${userName || 'User'}`;
                  setSelectedAvatar(url);
                }}
                className="w-full"
                variant="outline"
              >
                Use This Avatar
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="upload" className="space-y-4">
            <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg space-y-4">
              {selectedAvatar && !PRESET_AVATARS.includes(selectedAvatar) ? (
                <Avatar className="h-32 w-32">
                  <AvatarImage src={selectedAvatar} alt="Uploaded avatar" />
                  <AvatarFallback>{userName?.[0] || 'U'}</AvatarFallback>
                </Avatar>
              ) : (
                <div className="h-32 w-32 rounded-full bg-muted flex items-center justify-center">
                  <Upload className="h-12 w-12 text-muted-foreground" />
                </div>
              )}
              
              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  Upload a custom avatar image
                </p>
                <p className="text-xs text-muted-foreground">
                  Max 2MB, JPG, PNG or WEBP
                </p>
              </div>

              <label htmlFor="avatar-upload">
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  disabled={uploading}
                  className="sr-only"
                />
                <Button variant="outline" disabled={uploading} asChild>
                  <span>
                    {uploading ? 'Uploading...' : 'Choose File'}
                  </span>
                </Button>
              </label>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!selectedAvatar}>
            Save Avatar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
